import { useEffect } from "react";

/**
 * Core Web Vitals monitoring — zero dependencies, ~1 KB.
 *
 * Measures LCP, CLS, INP and TTFB with native PerformanceObserver and reports
 * each one once per page view as a Google Analytics `web_vitals` event
 * (G-CR7QGT89CB), so field data is visible per route instead of only in
 * lab/Lighthouse runs. Values are also exposed on `window.__vitals` for manual
 * inspection in the console.
 *
 * Everything runs after paint and inside try/catch: an unsupported entry type
 * must never break rendering.
 */

type Metric = { name: "LCP" | "CLS" | "INP" | "TTFB"; value: number; rating: "good" | "needs-improvement" | "poor" };

const THRESHOLDS: Record<Metric["name"], [number, number]> = {
  LCP: [2500, 4000],
  CLS: [0.1, 0.25],
  INP: [200, 500],
  TTFB: [800, 1800],
};

function rate(name: Metric["name"], value: number): Metric["rating"] {
  const [good, poor] = THRESHOLDS[name];
  return value <= good ? "good" : value <= poor ? "needs-improvement" : "poor";
}

export function WebVitals() {
  useEffect(() => {
    if (typeof window === "undefined" || typeof PerformanceObserver === "undefined") return;

    const store: Record<string, Metric> = ((window as any).__vitals ??= {});
    const sent = new Set<string>();

    const report = (name: Metric["name"], raw: number) => {
      const value = name === "CLS" ? Math.round(raw * 1000) / 1000 : Math.round(raw);
      store[name] = { name, value, rating: rate(name, value) };
      if (sent.has(name)) return;
      sent.add(name);
      const gtag = (window as any).gtag;
      if (typeof gtag !== "function") return;
      gtag("event", "web_vitals", {
        metric_name: name,
        metric_value: value,
        metric_rating: store[name]!.rating,
        page_path: window.location.pathname,
        non_interaction: true,
      });
    };

    const observers: PerformanceObserver[] = [];
    const observe = (type: string, cb: (entries: PerformanceEntryList) => void) => {
      try {
        const po = new PerformanceObserver((list) => cb(list.getEntries()));
        po.observe({ type, buffered: true } as PerformanceObserverInit);
        observers.push(po);
      } catch {
        /* entry type unsupported in this browser */
      }
    };

    // TTFB from the navigation timing entry.
    try {
      const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
      if (nav?.responseStart) report("TTFB", nav.responseStart);
    } catch {
      /* no navigation timing */
    }

    let lcp = 0;
    observe("largest-contentful-paint", (entries) => {
      const last = entries[entries.length - 1] as (PerformanceEntry & { renderTime?: number }) | undefined;
      if (last) lcp = last.renderTime ?? last.startTime;
    });

    let cls = 0;
    observe("layout-shift", (entries) => {
      for (const e of entries as (PerformanceEntry & { value: number; hadRecentInput: boolean })[]) {
        if (!e.hadRecentInput) cls += e.value;
      }
    });

    let inp = 0;
    observe("event", (entries) => {
      for (const e of entries as (PerformanceEntry & { interactionId?: number })[]) {
        if (e.interactionId && e.duration > inp) inp = e.duration;
      }
    });

    // Vitals are only final once the page is backgrounded or unloaded.
    const flush = () => {
      if (lcp) report("LCP", lcp);
      report("CLS", cls);
      if (inp) report("INP", inp);
    };
    const onHidden = () => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("pagehide", flush);

    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("pagehide", flush);
      for (const po of observers) po.disconnect();
    };
  }, []);

  return null;
}
